// @ts-check

import {
  addVehicleToRemoval,
  createVehicle,
  getVehicles,
  updateVehicle
} from '@/api/vehicle'

import { deserialize, serialize } from '@/serializers/vehicle'
import invariant from 'invariant'

/**
 * @returns {VehicleModuleState}
 */
export const initVehicleState = () => ({
  vehicles: null
})

/**
 * @type {VehicleGetterTree}
 */
export const vehicleGetters = {
  /**
   * Returns a flag indicating if the store module has fetched vehicles
   * at least once.
   */
  hasFetchedVehicles: (state) => !!state.vehicles,

  /**
   * Returns a flag indicating if the user has at least one valid vehicle.
   */
  hasValidVehicle: ({ vehicles }) => {
    if (!vehicles) return false
    return !!vehicles.find(({ state }) => state !== 'removed')
  },

  /**
   * Returns a vehicle from store module with the given id.
   */
  vehicleWithId:
    ({ vehicles }) =>
    (vehicleId) => {
      return vehicles?.find(({ id }) => id === vehicleId) ?? null
    }
}

/**
 * @type {VehicleMutationTree}
 */
export const vehicleMutations = {
  /**
   * Sets store module vehicles.
   */
  setVehicles(state, vehicles) {
    state.vehicles = vehicles
  },

  /**
   * Updates the given vehicle to store module.
   * @throws Will throw an error if updated vehicle does not exist in store.
   */
  updateVehicle(state, vehicle) {
    const { id: targetId } = vehicle
    invariant(state.vehicles, 'No vehicles!')

    const index = state.vehicles.findIndex(({ id }) => id === targetId)
    if (index < 0) {
      throw new Error(`Vehicle with id ${targetId} not found!`)
    }
    state.vehicles[index] = vehicle
  },

  /**
   * Adds the given vehicle to store module.
   */
  addVehicle(state, vehicle) {
    if (state.vehicles) {
      state.vehicles = [...state.vehicles, vehicle]
    } else {
      state.vehicles = [vehicle]
    }
  }
}

/**
 * @type {VehicleActionTree}
 */
export const vehicleActions = {
  async fetchVehicles({ commit, state }, companyId) {
    const { data: vehicles } = await getVehicles(companyId)
    commit('setVehicles', vehicles.map(deserialize))
    return state.vehicles
  },

  async createVehicle({ commit, getters }, { companyId, data }) {
    const { data: vehicle } = await createVehicle(companyId, serialize(data))
    commit('addVehicle', deserialize(vehicle))
    return getters.vehicleWithId(vehicle.id)
  },

  async updateVehicle({ commit, getters }, { companyId, vehicleId, data }) {
    const { data: vehicle } = await updateVehicle(
      companyId,
      vehicleId,
      serialize(data)
    )
    commit('updateVehicle', deserialize(vehicle))

    return getters.vehicleWithId(vehicle.id)
  },

  async addVehicleToRemoval(
    { commit, getters },
    { companyId, vehicleId, data }
  ) {
    const { data: vehicle } = await addVehicleToRemoval(
      companyId,
      vehicleId,
      serialize(data)
    )
    commit('updateVehicle', deserialize(vehicle))
    return getters.vehicleWithId(vehicle.id)
  }
}

export const vehicleModule = {
  namespaced: true,
  state: initVehicleState,
  getters: vehicleGetters,
  mutations: vehicleMutations,
  actions: vehicleActions
}

export default vehicleModule
